/**
 * EmailTemplatesPage.tsx — Email template management for org admins
 *
 * Features:
 *   - List all email templates (grouped by category)
 *   - Edit subject/HTML/text with live preview
 *   - Variable reference panel per template
 *   - Send test email
 *   - Reset to platform default
 *   - Version indicator
 *
 * @module packages/billing-admin/src/pages/EmailTemplatesPage
 * @since 2026-06-20 (Platform Upgrade — Phase 1.11)
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EmailTemplate {
  id: string;
  templateKey: string;
  name: string;
  category: string;
  subjectTemplate: string;
  htmlTemplate: string;
  textTemplate: string | null;
  isActive: boolean;
  version: number;
  availableVariables?: string[];
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<string, string> = {
  billing: 'Billing',
  lifecycle: 'Lifecycle',
  dunning: 'Dunning',
  system: 'System',
};

const CATEGORY_COLORS: Record<string, string> = {
  billing: 'bg-blue-100 text-blue-700',
  lifecycle: 'bg-emerald-100 text-emerald-700',
  dunning: 'bg-amber-100 text-amber-700',
  system: 'bg-purple-100 text-purple-700',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EmailTemplatesPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const orgId = activeOrg?.orgId ?? '';
  const queryClient = useQueryClient();

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);

  // Form state
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [editText, setEditText] = useState('');

  // ─── Data fetching ─────────────────────────────────────────────────────
  const { data: templates, isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ['org', orgId, 'email-templates'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const body = await res.json();
      return body.templates;
    },
    enabled: !!token && !!orgId,
  });

  // ─── Mutations ─────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (payload: { subjectTemplate: string; htmlTemplate: string; textTemplate: string | null }) => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-templates/${selectedTemplate!.templateKey}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'email-templates'] });
      setEditMode(false);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-templates/${selectedTemplate!.templateKey}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Reset failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'email-templates'] });
      setSelectedTemplate(null);
      setShowResetConfirm(false);
    },
  });

  const testSendMutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/email-templates/${selectedTemplate!.templateKey}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: email }),
      });
      if (!res.ok) throw new Error(`Send failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      setShowTestDialog(false);
      setTestEmail('');
    },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleSelectTemplate = useCallback((t: EmailTemplate) => {
    setSelectedTemplate(t);
    setEditSubject(t.subjectTemplate);
    setEditHtml(t.htmlTemplate);
    setEditText(t.textTemplate ?? '');
    setEditMode(false);
    setShowPreview(false);
  }, []);

  const handleSave = useCallback(() => {
    saveMutation.mutate({
      subjectTemplate: editSubject,
      htmlTemplate: editHtml,
      textTemplate: editText || null,
    });
  }, [editSubject, editHtml, editText, saveMutation]);

  // ─── Group templates by category ──────────────────────────────────────
  const grouped = (templates ?? []).reduce<Record<string, EmailTemplate[]>>((acc, t) => {
    const cat = t.category || 'system';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <AdminLayout title="Email Templates">
      <div className="flex gap-6 h-[calc(100vh-8rem)]">
        {/* ─── Template List (Left Panel) ─────────────────────────────── */}
        <div className="w-80 flex-shrink-0 overflow-y-auto rounded-xl border border-slate-200 bg-white">
          <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 backdrop-blur px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">Templates</h3>
            <p className="text-xs text-slate-500 mt-0.5">{templates?.length ?? 0} templates</p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category} className="py-2">
                  <p className="px-4 py-1 text-xs font-medium uppercase tracking-wider text-slate-400">
                    {CATEGORY_LABELS[category] ?? category}
                  </p>
                  {items.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleSelectTemplate(t)}
                      className={`w-full px-4 py-2 text-left transition-colors hover:bg-slate-50 ${
                        selectedTemplate?.id === t.id ? 'bg-cyan-50 border-l-2 border-cyan-500' : ''
                      }`}
                    >
                      <p className="text-sm font-medium text-slate-800 truncate">{t.name}</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${CATEGORY_COLORS[category] ?? 'bg-slate-100 text-slate-600'}`}>
                          {category}
                        </span>
                        <span className="text-[10px] text-slate-400">v{t.version}</span>
                        {!t.isActive && <span className="text-[10px] text-red-400">disabled</span>}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── Template Detail (Right Panel) ──────────────────────────── */}
        <div className="flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white">
          {!selectedTemplate ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="rounded-full bg-slate-100 p-4 mb-4">
                <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">Select a template</p>
              <p className="mt-1 text-xs text-slate-400">Choose a template from the list to view or edit its content.</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Header bar */}
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{selectedTemplate.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Key: <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">{selectedTemplate.templateKey}</code>
                    {' · '}Version {selectedTemplate.version}
                    {' · '}Updated {new Date(selectedTemplate.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!editMode ? (
                    <>
                      <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        {showPreview ? 'Hide Preview' : 'Preview'}
                      </button>
                      <button
                        onClick={() => setShowTestDialog(true)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        Test Send
                      </button>
                      <button
                        onClick={() => setEditMode(true)}
                        className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-700"
                      >
                        Edit
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Reset to Default
                      </button>
                      <button
                        onClick={() => setEditMode(false)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saveMutation.isPending}
                        className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                      >
                        {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Variable Reference */}
                {selectedTemplate.availableVariables && selectedTemplate.availableVariables.length > 0 && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <p className="text-xs font-medium text-slate-600 mb-1.5">Available Variables</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTemplate.availableVariables.map((v) => (
                        <code key={v} className="rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-700">
                          {'{{' + v + '}}'}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Subject Line</label>
                  {editMode ? (
                    <input
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    />
                  ) : (
                    <p className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-800">
                      {selectedTemplate.subjectTemplate}
                    </p>
                  )}
                </div>

                {/* HTML Template */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">HTML Template</label>
                  {editMode ? (
                    <textarea
                      value={editHtml}
                      onChange={(e) => setEditHtml(e.target.value)}
                      rows={16}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    />
                  ) : showPreview ? (
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <div className="bg-slate-100 px-3 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                        Live Preview
                      </div>
                      <iframe
                        srcDoc={selectedTemplate.htmlTemplate}
                        title="Template Preview"
                        className="w-full h-96 border-0"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <pre className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 font-mono text-xs text-slate-700 overflow-auto max-h-80">
                      {selectedTemplate.htmlTemplate}
                    </pre>
                  )}
                </div>

                {/* Plain Text Template */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Plain Text (Fallback)</label>
                  {editMode ? (
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={6}
                      placeholder="Optional plain-text version for email clients that don't support HTML"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    />
                  ) : (
                    <pre className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 font-mono text-xs text-slate-600 overflow-auto max-h-40">
                      {selectedTemplate.textTemplate || '(no plain-text version)'}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Test Send Dialog ──────────────────────────────────────────── */}
      {showTestDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">Send Test Email</h3>
            <p className="mt-1 text-sm text-slate-500">
              Send a test render of "{selectedTemplate?.name}" with sample variables.
            </p>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowTestDialog(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => testSendMutation.mutate(testEmail)}
                disabled={!testEmail || testSendMutation.isPending}
                className="rounded-lg bg-cyan-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
              >
                {testSendMutation.isPending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
            {testSendMutation.isError && (
              <p className="mt-2 text-xs text-red-600">{String(testSendMutation.error)}</p>
            )}
            {testSendMutation.isSuccess && (
              <p className="mt-2 text-xs text-green-600">Test email sent successfully!</p>
            )}
          </div>
        </div>
      )}

      {/* ─── Reset Confirmation Dialog ─────────────────────────────────── */}
      <ConfirmDialog
        open={showResetConfirm}
        title="Reset Template to Default"
        message={`This will discard all customizations for "${selectedTemplate?.name}" and restore the platform default. This action cannot be undone.`}
        confirmLabel="Reset"
        variant="danger"
        onConfirm={() => resetMutation.mutate()}
        onCancel={() => setShowResetConfirm(false)}
        loading={resetMutation.isPending}
      />
    </AdminLayout>
  );
}
